import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import { supplierService } from '../../services/supplierService';
import NewSupplierModal from './components/NewSupplierModal';

const SupplierRegistrationAndManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService?.getAll();
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (value?.trim()) {
      try {
        const results = await supplierService?.search(value);
        setSuppliers(results || []);
      } catch (error) {
        console.error('Error searching suppliers:', error);
      }
    } else {
      loadSuppliers();
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este fornecedor?')) return;

    try {
      await supplierService?.delete(id);
      loadSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      alert('Erro ao excluir fornecedor: ' + error?.message);
    }
  };

  const filteredSuppliers = searchTerm
    ? suppliers
    : suppliers;

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Fornecedores</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie seus fornecedores de forma simples e rápida
            </p>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Icon name="Plus" size={18} />
            <span>Novo Fornecedor</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Icon
            name="Search"
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e?.target?.value)}
            placeholder="Pesquisar por nome, razão social ou CNPJ..."
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Suppliers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : filteredSuppliers?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Nenhum fornecedor encontrado
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm
              ? 'Tente buscar por outro termo' :'Comece cadastrando seu primeiro fornecedor'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowNewModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
            >
              <Icon name="Plus" size={18} />
              <span>Novo Fornecedor</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                  Fornecedor
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                  Razão Social
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                  CNPJ
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">
                  Endereço
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-foreground">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredSuppliers?.map((supplier) => (
                <tr
                  key={supplier?.id}
                  className="hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => setSelectedSupplier(supplier)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Package" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{supplier?.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-foreground">
                      {supplier?.razaoSocial || '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-foreground font-mono">
                      {supplier?.cnpj || '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {supplier?.address || '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDelete(supplier?.id);
                        }}
                        className="p-2 text-muted-foreground hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <NewSupplierModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={loadSuppliers}
      />

      {/* Supplier Details Modal */}
      {selectedSupplier && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSupplier(null)}
        >
          <div
            className="bg-card rounded-lg shadow-xl max-w-2xl w-full p-6"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                Detalhes do Fornecedor
              </h2>
              <button
                onClick={() => setSelectedSupplier(null)}
                className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Nome do Fornecedor
                </label>
                <p className="text-foreground font-medium mt-1">
                  {selectedSupplier?.name}
                </p>
              </div>

              {selectedSupplier?.razaoSocial && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Razão Social
                  </label>
                  <p className="text-foreground mt-1">
                    {selectedSupplier?.razaoSocial}
                  </p>
                </div>
              )}

              {selectedSupplier?.cnpj && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    CNPJ
                  </label>
                  <p className="text-foreground font-mono mt-1">
                    {selectedSupplier?.cnpj}
                  </p>
                </div>
              )}

              {selectedSupplier?.address && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Endereço
                  </label>
                  <p className="text-foreground mt-1">
                    {selectedSupplier?.address}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierRegistrationAndManagement;